import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {UserService} from '../../services/user/user.service';
import {MessageService} from '../../services/message/message.service';
import {NavigationService} from '../../services/nav/navigation.service';
import {confirmResetPassword, getCurrentUser, updatePassword} from '@aws-amplify/auth';
import {UserDto} from '@api/utisha-backend';
import { UtButtonComponent } from '../../ui';
import { TranslatePipe } from '@ngx-translate/core';
import { InputComponent } from '../../form/input/input.component';
import { NgTemplateOutlet } from '@angular/common';

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    imports: [UtButtonComponent, TranslatePipe, FormsModule, ReactiveFormsModule, InputComponent, NgTemplateOutlet]
})
export class ChangePasswordComponent implements OnInit {

  @Input() buttonLabel: string;
  @Output() success = new EventEmitter();
  @Output() cancel = new EventEmitter();
  error: string;
  unauthform: UntypedFormGroup;
  authform: UntypedFormGroup;
  nav: NavigationService;
  user: UserDto;
  loading = true;
  private code: string | undefined;
  private email: string | undefined;

  constructor(private formBuilder: UntypedFormBuilder,
              private userService: UserService,
              private navService: NavigationService,
              private messageService: MessageService,
              private activatedRoute: ActivatedRoute) {
    this.nav = navService;
  }

  showErrors(ctrl: AbstractControl): boolean {
    return !!ctrl?.errors && ctrl.touched;
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    const unauth = !!this.getQueryParam('code');
    if (unauth) {
      this.initForUnauthenticatedUser();
    } else {
      this.user = await this.userService.getUser()
      this.initForAuthenticatedUser();
    }
    this.loading = false;
  }

  async submit(): Promise<void> {
    if (this.user) {
      await this.submitForAuth();
    } else {
      await this.submitForUnauth();
    }

  }

  private async submitForUnauth(): Promise<void> {
    if (this.unauthform.valid) {
      try {
        await confirmResetPassword({
          username: this.email!,
          confirmationCode: this.code!,
          newPassword: this.unauthform.controls.password.value
        });
        this.messageService.success('password.updated');
        this.success.emit();
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred';
        this.messageService.error(errorMessage);
      }
    }
  }

  private async submitForAuth(): Promise<void> {
    if (this.authform.valid) {
      try {
        await updatePassword({
          newPassword: this.authform.controls.newPassword.value,
          oldPassword: this.authform.controls.oldPassword.value
        });
        this.messageService.success('password.updated');
        this.success.emit();
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred';
        this.messageService.error(errorMessage);
      }
    }
  }

  get activeForm(): UntypedFormGroup {
    return this.user ? this.authform : this.unauthform;
  }

  private initForAuthenticatedUser(): void {
    this.authform = this.formBuilder.group({
      oldPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required]]
    });
  }

  getQueryParam(queryParamName: string): string | undefined {
    const queryString = window.location.search;
    if (queryString.indexOf('?') !== 0) {
      return undefined;
    }
    const params = queryString.substring(1).split('&');
    for (const param of params) {
      const [paramName, paramValue] = param.split('=');
      if (paramName === queryParamName) {
        return paramValue;
      }
    }
    return undefined;
  }

  private initForUnauthenticatedUser(): void {
    this.email = this.getQueryParam('email');
    this.code = this.getQueryParam('code');
    if (!this.email || !this.code) {
      this.messageService.error('No email and/or reset password code provided!');
      this.cancel.emit();
    }
    this.unauthform = this.formBuilder.group({
      password: ['', [Validators.required]]
    });
  }

}
